import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Shield,
  Zap,
  Coins,
  Users,
  ArrowLeftRight,
  CheckCircle,
  ChevronDown,
  Hexagon,
  Globe,
  Lock,
  TrendingUp,
  Layers,
  BarChart3,
  Sparkles,
} from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import { useTheme } from '@/contexts/ThemeContext'
import { cn } from '@/lib/utils'

// Animated counter hook
function useCountUp(end: number, duration: number = 2000, startOnView: boolean = true) {
  const ref = useRef<HTMLSpanElement>(null)
  const countRef = useRef(0)
  const hasAnimated = useRef(false)

  useEffect(() => {
    if (!ref.current || !startOnView) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true
            const startTime = Date.now()
            const animate = () => {
              const elapsed = Date.now() - startTime
              const progress = Math.min(elapsed / duration, 1)
              const easeOut = 1 - Math.pow(1 - progress, 3)
              countRef.current = Math.floor(end * easeOut)
              if (ref.current) {
                ref.current.textContent = countRef.current.toLocaleString()
              }
              if (progress < 1) {
                requestAnimationFrame(animate)
              }
            }
            requestAnimationFrame(animate)
          }
        })
      },
      { threshold: 0.5 }
    )

    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [end, duration, startOnView])

  return ref
}

// Floating hexagon component
function FloatingHexagon({ className, delay = 0 }: { className?: string; delay?: number }) {
  return (
    <div
      className={cn('absolute opacity-10', className)}
      style={{
        animation: `float ${8 + delay}s ease-in-out infinite`,
        animationDelay: `${delay}s`,
      }}
    >
      <Hexagon className="w-full h-full stroke-uni-500" strokeWidth={0.5} />
    </div>
  )
}

export function LandingPage() {
  const { t } = useLanguage()
  const { theme } = useTheme()

  const volumeRef = useCountUp(2500000)
  const txRef = useCountUp(15420)
  const usersRef = useCountUp(3250)

  return (
    <div className="relative overflow-hidden">
      {/* Animated background elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <FloatingHexagon className="w-64 h-64 -top-20 -left-20" delay={0} />
        <FloatingHexagon className="w-48 h-48 top-1/4 right-10" delay={2} />
        <FloatingHexagon className="w-32 h-32 top-1/2 left-1/4" delay={1} />
        <FloatingHexagon className="w-56 h-56 bottom-20 right-1/4" delay={3} />
        <FloatingHexagon className="w-40 h-40 bottom-1/3 -left-10" delay={1.5} />

        {/* Gradient orbs */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-uni-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-uni-600/8 rounded-full blur-[100px]" />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] sm:min-h-[90vh] flex items-center justify-center px-4 pt-12 sm:pt-20 pb-16 sm:pb-32">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          {/* Glowing badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-uni-500/10 border border-uni-500/20 mb-8 animate-fade-in-up">
            <div className="w-2 h-2 rounded-full bg-uni-400 animate-pulse" />
            <span className="text-sm text-uni-400 font-medium tracking-wide">Wrapped USDT on Uni2</span>
          </div>

          {/* Main title */}
          <h1 className="font-display text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-6 animate-fade-in-up animation-delay-100">
            <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{t('heroTitle')}</span>
            <br />
            <span className="relative">
              <span className="text-gradient">{t('heroTitleHighlight')}</span>
              <svg
                className="absolute -bottom-2 left-0 w-full"
                viewBox="0 0 300 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 10C50 4 100 2 150 6C200 10 250 4 298 8"
                  stroke="url(#underline-gradient)"
                  strokeWidth="3"
                  strokeLinecap="round"
                  className="animate-draw-line"
                />
                <defs>
                  <linearGradient id="underline-gradient" x1="0" y1="0" x2="300" y2="0">
                    <stop stopColor="#00ff66" />
                    <stop offset="1" stopColor="#2bfd7a" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h1>

          {/* Subtitle */}
          <p className={cn(
            'text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up animation-delay-200',
            theme === 'dark' ? 'text-dark-300' : 'text-gray-600'
          )}>
            {t('heroSubtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up animation-delay-300">
            <a
              href="#what-is"
              className="group relative inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-uni-600 to-uni-500 text-dark-950 font-semibold rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-glow-lg hover:scale-105"
            >
              <span className="relative z-10">{t('learnMore')}</span>
              <ChevronDown className="w-5 h-5 relative z-10 transition-transform group-hover:translate-y-1" />
              <div className="absolute inset-0 bg-gradient-to-r from-uni-500 to-uni-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
            <Link
              to="/bridge"
              className={cn(
                'inline-flex items-center gap-2 px-8 py-4 font-medium rounded-2xl border transition-all duration-300',
                theme === 'dark'
                  ? 'text-dark-200 border-dark-700 hover:border-dark-600 hover:bg-dark-800/50'
                  : 'text-gray-600 border-gray-300 hover:border-gray-400 hover:bg-gray-100/50'
              )}
            >
              {t('startBridge')}
              <ArrowLeftRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Token visual */}
          <div className="mt-16 flex items-center justify-center animate-fade-in-up animation-delay-400">
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-uni-500/20 rounded-full blur-2xl scale-150" />

              {/* Main token */}
              <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-uni-500 to-uni-600 flex items-center justify-center shadow-glow-lg">
                <div className="text-center">
                  <div className="text-3xl font-display font-bold text-dark-950">W2</div>
                  <div className="text-sm font-semibold text-dark-900">USDT</div>
                </div>
              </div>

              {/* Orbiting elements */}
              <div className="absolute -top-2 -right-2 px-2 py-1 rounded-lg bg-dark-800 border border-dark-700 text-xs text-uni-400 font-medium">
                1:1 USD
              </div>
              <div className="absolute -bottom-2 -left-2 px-2 py-1 rounded-lg bg-dark-800 border border-dark-700 text-xs text-dark-300 flex items-center gap-1">
                <Lock className="w-3 h-3 text-uni-500" />
                Stable
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-fade-in animation-delay-500">
          <div className={cn(
            'w-6 h-10 rounded-full border-2 flex items-start justify-center p-1',
            theme === 'dark' ? 'border-dark-600' : 'border-gray-300'
          )}>
            <div className="w-1.5 h-3 rounded-full bg-uni-500 animate-scroll-down" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: volumeRef, prefix: '$', suffix: '+', label: t('statVolume') },
              { value: txRef, prefix: '', suffix: '+', label: t('statTransactions') },
              { value: usersRef, prefix: '', suffix: '+', label: t('statUsers') },
              { value: null, static: '99.9%', label: t('statUptime') },
            ].map((stat, i) => (
              <div
                key={i}
                className={cn(
                  'relative group p-6 rounded-2xl border backdrop-blur-sm hover:border-uni-500/30 transition-all duration-300',
                  theme === 'dark'
                    ? 'bg-dark-900/40 border-dark-800/50'
                    : 'bg-white/60 border-gray-200'
                )}
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-uni-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <div className={cn(
                    'text-3xl md:text-4xl font-display font-bold mb-1',
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  )}>
                    {stat.prefix}
                    {stat.value ? <span ref={stat.value}>0</span> : stat.static}
                    {stat.suffix}
                  </div>
                  <div className={cn('text-sm', theme === 'dark' ? 'text-dark-400' : 'text-gray-500')}>{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What is W2USDT Section */}
      <section id="what-is" className="relative py-16 sm:py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{t('whatIsTitle')} </span>
                <span className="text-gradient">{t('whatIsW2USDT')}</span>
                <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>?</span>
              </h2>
              <p className={cn('text-lg leading-relaxed mb-6', theme === 'dark' ? 'text-dark-300' : 'text-gray-600')}>
                {t('whatIsDesc')}
              </p>
              <p className={cn('leading-relaxed', theme === 'dark' ? 'text-dark-400' : 'text-gray-500')}>
                {t('whatIsDesc2')}
              </p>

              {/* Key points */}
              <div className="mt-8 space-y-3">
                {[
                  { icon: Shield, text: 'Paridad 1:1 con USDT' },
                  { icon: Zap, text: 'Red Uni2 ultrarrápida' },
                  { icon: Users, text: 'Comunidad en crecimiento' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-uni-500/15 flex items-center justify-center">
                      <item.icon className="w-4 h-4 text-uni-500" />
                    </div>
                    <span className={cn('font-medium', theme === 'dark' ? 'text-dark-200' : 'text-gray-700')}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              {/* Token visual */}
              <div className="relative w-64 h-64 mx-auto">
                {/* Outer glow rings */}
                <div className="absolute inset-0 rounded-full border border-uni-500/20 animate-ping-slow" />
                <div className="absolute inset-4 rounded-full border border-uni-500/30 animate-ping-slow animation-delay-200" />
                <div className="absolute inset-8 rounded-full border border-uni-500/40 animate-ping-slow animation-delay-400" />

                {/* Main token */}
                <div className="absolute inset-12 rounded-full bg-gradient-to-br from-uni-500 to-uni-600 flex items-center justify-center shadow-glow-lg">
                  <div className="text-center">
                    <div className="text-4xl font-display font-bold text-dark-950">W2</div>
                    <div className="text-sm font-semibold text-dark-900">USDT</div>
                  </div>
                </div>

                {/* Floating elements */}
                <div className={cn(
                  'absolute -top-4 -right-4 px-3 py-1.5 rounded-lg border text-xs',
                  theme === 'dark'
                    ? 'bg-dark-800 border-dark-700 text-dark-300'
                    : 'bg-white border-gray-200 text-gray-600'
                )}>
                  1:1 USD
                </div>
                <div className={cn(
                  'absolute -bottom-4 -left-4 px-3 py-1.5 rounded-lg border text-xs flex items-center gap-1',
                  theme === 'dark'
                    ? 'bg-dark-800 border-dark-700 text-dark-300'
                    : 'bg-white border-gray-200 text-gray-600'
                )}>
                  <Lock className="w-3 h-3 text-uni-500" />
                  Stable
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-16 sm:py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{t('featuresTitle')} </span>
              <span className="text-gradient">{t('featuresTitleHighlight')}</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Shield,
                title: t('featureSecure'),
                desc: t('featureSecureDesc'),
                gradient: 'from-emerald-500 to-green-600',
              },
              {
                icon: Zap,
                title: t('featureFast'),
                desc: t('featureFastDesc'),
                gradient: 'from-yellow-500 to-orange-500',
              },
              {
                icon: Coins,
                title: t('featureLowFee'),
                desc: t('featureLowFeeDesc'),
                gradient: 'from-uni-500 to-uni-600',
              },
              {
                icon: Users,
                title: t('featureSupport'),
                desc: t('featureSupportDesc'),
                gradient: 'from-blue-500 to-indigo-600',
              },
            ].map((feature, i) => (
              <div
                key={i}
                className={cn(
                  'group relative p-8 rounded-2xl border backdrop-blur-sm transition-all duration-500 overflow-hidden',
                  theme === 'dark'
                    ? 'bg-dark-900/40 border-dark-800/50 hover:border-dark-700'
                    : 'bg-white/60 border-gray-200 hover:border-gray-300'
                )}
              >
                {/* Hover gradient */}
                <div className={cn(
                  'absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-500',
                  `bg-gradient-to-br ${feature.gradient}`
                )} />

                <div className="relative">
                  <div className={cn(
                    'w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110',
                    `bg-gradient-to-br ${feature.gradient}`
                  )}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className={cn(
                    'text-xl font-display font-semibold mb-3',
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  )}>
                    {feature.title}
                  </h3>
                  <p className={cn('leading-relaxed', theme === 'dark' ? 'text-dark-400' : 'text-gray-500')}>
                    {feature.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ecosystem Section */}
      <section className="relative py-16 sm:py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{t('ecosystemTitle')} </span>
              <span className="text-gradient">{t('ecosystemTitleHighlight')}</span>
            </h2>
            <p className={cn('text-lg max-w-2xl mx-auto', theme === 'dark' ? 'text-dark-300' : 'text-gray-600')}>
              {t('ecosystemDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Globe,
                title: t('ecosystemFeature1'),
                desc: t('ecosystemFeature1Desc'),
              },
              {
                icon: Layers,
                title: t('ecosystemFeature2'),
                desc: t('ecosystemFeature2Desc'),
              },
              {
                icon: TrendingUp,
                title: t('ecosystemFeature3'),
                desc: t('ecosystemFeature3Desc'),
              },
            ].map((item, i) => (
              <div
                key={i}
                className={cn(
                  'text-center p-8 rounded-2xl border transition-all duration-300',
                  theme === 'dark'
                    ? 'bg-dark-900/40 border-dark-800/50 hover:border-uni-500/30'
                    : 'bg-white/60 border-gray-200 hover:border-uni-500/30'
                )}
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-uni-500 to-uni-600 flex items-center justify-center mx-auto mb-5 shadow-glow-sm">
                  <item.icon className="w-8 h-8 text-dark-950" />
                </div>
                <h3 className={cn(
                  'text-xl font-display font-semibold mb-3',
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                )}>
                  {item.title}
                </h3>
                <p className={cn('text-sm leading-relaxed', theme === 'dark' ? 'text-dark-400' : 'text-gray-500')}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bridge Section */}
      <section className="relative py-16 sm:py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <div className={cn(
            'relative overflow-hidden rounded-3xl border',
            theme === 'dark'
              ? 'bg-dark-900/60 border-dark-800'
              : 'bg-white/80 border-gray-200'
          )}>
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 right-0 w-64 h-64 bg-uni-500/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-yellow-500/10 rounded-full blur-3xl" />
            </div>

            <div className="relative p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
                    <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{t('bridgeTitle')} </span>
                    <span className="text-gradient">{t('bridgeTitleHighlight')}</span>
                  </h2>
                  <p className={cn('text-lg mb-6', theme === 'dark' ? 'text-dark-300' : 'text-gray-600')}>
                    {t('bridgeDesc')}
                  </p>

                  <div className="flex flex-wrap gap-3 mb-8">
                    {[t('bridgeFeature1'), t('bridgeFeature2'), t('bridgeFeature3')].map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-uni-500" />
                        <span className={cn('font-medium', theme === 'dark' ? 'text-dark-200' : 'text-gray-700')}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/bridge"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-uni-600 to-uni-500 text-dark-950 font-semibold rounded-xl transition-all duration-300 hover:shadow-glow hover:scale-105"
                  >
                    {t('bridgeButton')}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>

                {/* Bridge visual */}
                <div className="flex items-center justify-center gap-4">
                  <div className={cn(
                    'flex items-center gap-3 px-5 py-4 rounded-xl border',
                    theme === 'dark'
                      ? 'bg-dark-800/80 border-dark-700'
                      : 'bg-gray-50 border-gray-200'
                  )}>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-uni-500 to-uni-600 flex items-center justify-center">
                      <span className="font-bold text-dark-950">W2</span>
                    </div>
                    <div className="text-left">
                      <div className={cn('font-semibold', theme === 'dark' ? 'text-white' : 'text-gray-900')}>W2USDT</div>
                      <div className={cn('text-xs', theme === 'dark' ? 'text-dark-400' : 'text-gray-500')}>Uni2</div>
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-1">
                    <ArrowRight className="w-6 h-6 text-uni-500" />
                  </div>

                  <div className={cn(
                    'flex items-center gap-3 px-5 py-4 rounded-xl border',
                    theme === 'dark'
                      ? 'bg-dark-800/80 border-dark-700'
                      : 'bg-gray-50 border-gray-200'
                  )}>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center">
                      <span className="font-bold text-dark-950">₮</span>
                    </div>
                    <div className="text-left">
                      <div className={cn('font-semibold', theme === 'dark' ? 'text-white' : 'text-gray-900')}>USDT</div>
                      <div className={cn('text-xs', theme === 'dark' ? 'text-dark-400' : 'text-gray-500')}>BSC</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="relative py-16 sm:py-24 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{t('communityTitle')} </span>
            <span className="text-gradient">{t('communityTitleHighlight')}</span>
          </h2>
          <p className={cn('text-lg max-w-2xl mx-auto mb-10', theme === 'dark' ? 'text-dark-300' : 'text-gray-600')}>
            {t('communityDesc')}
          </p>

          {/* Community stats */}
          <div className="flex flex-wrap items-center justify-center gap-8 mb-10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-uni-500/15 flex items-center justify-center">
                <Users className="w-6 h-6 text-uni-500" />
              </div>
              <div className="text-left">
                <div className={cn('text-2xl font-bold', theme === 'dark' ? 'text-white' : 'text-gray-900')}>3,000+</div>
                <div className={cn('text-sm', theme === 'dark' ? 'text-dark-400' : 'text-gray-500')}>Holders</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-uni-500/15 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-uni-500" />
              </div>
              <div className="text-left">
                <div className={cn('text-2xl font-bold', theme === 'dark' ? 'text-white' : 'text-gray-900')}>$2.5M+</div>
                <div className={cn('text-sm', theme === 'dark' ? 'text-dark-400' : 'text-gray-500')}>Volumen</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-uni-500/15 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-uni-500" />
              </div>
              <div className="text-left">
                <div className={cn('text-2xl font-bold', theme === 'dark' ? 'text-white' : 'text-gray-900')}>24/7</div>
                <div className={cn('text-sm', theme === 'dark' ? 'text-dark-400' : 'text-gray-500')}>Activo</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 sm:py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl">
            {/* Background effects */}
            <div className={cn(
              'absolute inset-0 bg-gradient-to-br',
              theme === 'dark'
                ? 'from-uni-600/20 via-dark-900 to-dark-950'
                : 'from-uni-500/10 via-gray-50 to-white'
            )} />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMGZmNjYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />

            <div className="relative p-12 md:p-16 text-center">
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
                <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{t('ctaTitle')} </span>
                <span className="text-gradient">{t('ctaTitleHighlight')}</span>
              </h2>
              <p className={cn('text-lg max-w-xl mx-auto mb-10', theme === 'dark' ? 'text-dark-300' : 'text-gray-600')}>
                {t('ctaDesc')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/bridge"
                  className="group inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-uni-600 to-uni-500 text-dark-950 font-bold text-lg rounded-2xl transition-all duration-300 hover:shadow-glow-lg hover:scale-105"
                >
                  {t('ctaButton')}
                  <ArrowRight className="w-6 h-6 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer spacing */}
      <div className="h-16" />

      {/* Custom styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scroll-down {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(6px); opacity: 0.5; }
        }

        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(4px); }
        }

        @keyframes draw-line {
          from { stroke-dashoffset: 400; }
          to { stroke-dashoffset: 0; }
        }

        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.3; }
          100% { transform: scale(1); opacity: 0.5; }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-scroll-down {
          animation: scroll-down 1.5s ease-in-out infinite;
        }

        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }

        .animate-draw-line {
          stroke-dasharray: 400;
          animation: draw-line 1.5s ease-out forwards;
          animation-delay: 0.5s;
        }

        .animate-ping-slow {
          animation: ping-slow 3s ease-in-out infinite;
        }

        .animation-delay-100 { animation-delay: 0.1s; }
        .animation-delay-200 { animation-delay: 0.2s; }
        .animation-delay-300 { animation-delay: 0.3s; }
        .animation-delay-400 { animation-delay: 0.4s; }
        .animation-delay-500 { animation-delay: 0.5s; }
      `}</style>
    </div>
  )
}
